/**
 * `Email/set` and `EmailSubmission/set`, and the optimistic-update discipline
 * that goes with them.
 *
 * This file exists because the client now *changes* things, and every place it
 * does needs the same three properties. Writing them once is the difference
 * between a UI that is wrong in one way and a UI that is wrong in five.
 *
 * # Why optimistic at all
 *
 * The daemon applies a change locally and drains it upstream later -- see
 * `hm_store::outbox`. So the round trip we are hiding is the *local* one, a few
 * milliseconds, and the honest thing would be to just await it. We do not,
 * because a list that repaints 80ms after the click reads as lag on a touch
 * screen, and because the archive-then-immediately-archive-the-next-one rhythm
 * is the single most common thing anyone does with a mailbox.
 *
 * What we must not do is the version of optimism that lies. Every helper here
 * takes a snapshot, applies the change, and restores the snapshot if the server
 * disagrees -- and the restore is not best-effort, because a message that
 * silently stays archived when the server refused is a message the user will
 * not see again.
 *
 * # Partial failure is normal, not exceptional
 *
 * `Email/set` reports per-object outcomes: `updated` and `notUpdated` arrive in
 * the same response, and one bad id never fails the batch. Select forty
 * messages, archive them, and it is entirely ordinary for thirty-nine to move
 * and one to come back `notFound` because it was deleted from a phone thirty
 * seconds ago. So the return type here is a *split*, never a boolean, and
 * callers are expected to roll back only the objects that actually failed.
 *
 * # `ifInState` is deliberately not used for these
 *
 * RFC 8620 offers it, and it is the right tool for a client that holds a whole
 * mailbox and wants to refuse a write against a stale view. Ours does not: it
 * holds a page of forty rows, and the state changes every time *any* account
 * receives *any* message. Passing `ifInState` would mean a background delivery
 * during composition rejects the archive the user just clicked, which is a
 * conflict that is not one. Per-object `notUpdated` already reports the
 * conflicts that matter.
 */

import { call, SUBMISSION, type Invocation } from "./jmap";
import type { Email } from "./types";

/** A JMAP `SetError`, as it arrives in `notUpdated` / `notCreated`. */
export interface SetError {
  type: string;
  description?: string | null;
  /** Present on `invalidProperties`; names the properties at fault. */
  properties?: string[];
}

/**
 * What a `/set` did, per object.
 *
 * `ok` holds the ids the server accepted; `failed` maps a rejected id to why.
 * Both can be non-empty at once, and callers must handle that rather than
 * treating a non-empty `failed` as total failure.
 */
export interface SetOutcome {
  ok: string[];
  failed: Map<string, SetError>;
  /** The account's new state string, for callers tracking it. */
  newState: string | null;
}

const EMPTY: SetOutcome = { ok: [], failed: new Map(), newState: null };

function outcomeFrom(
  args: Record<string, unknown> | undefined,
  okKey: "updated" | "destroyed" | "created",
  failKey: "notUpdated" | "notDestroyed" | "notCreated",
): SetOutcome {
  if (!args) return EMPTY;

  // `destroyed` is an array of ids; `updated` and `created` are objects keyed
  // by id. Both shapes mean the same thing here.
  const okRaw = args[okKey];
  const ok = Array.isArray(okRaw)
    ? (okRaw as string[])
    : okRaw && typeof okRaw === "object"
      ? Object.keys(okRaw)
      : [];

  const failed = new Map<string, SetError>();
  const failRaw = args[failKey];
  if (failRaw && typeof failRaw === "object") {
    for (const [id, err] of Object.entries(failRaw as Record<string, SetError>)) {
      failed.set(id, err);
    }
  }

  return { ok, failed, newState: (args.newState as string | undefined) ?? null };
}

/**
 * A `/set` call that failed as a whole rather than per object.
 *
 * Distinguished from per-object failure because the two need opposite
 * responses: roll back the objects named, versus roll back everything.
 */
export class SetFailed extends Error {
  constructor(readonly detail: Record<string, unknown>) {
    super(
      typeof detail.description === "string"
        ? detail.description
        : `the server refused the change (${String(detail.type ?? "unknown")})`,
    );
    this.name = "SetFailed";
  }
}

function requireOk(args: Record<string, unknown> | undefined, callId: string): Record<string, unknown> {
  if (!args) throw new SetFailed({ type: "serverSkippedCall", description: `no response for ${callId}` });
  if (args.__error) throw new SetFailed(args.__error as Record<string, unknown>);
  return args;
}

// ---- updates -----------------------------------------------------------------

/**
 * A patch to apply to one message.
 *
 * Patch syntax (`"keywords/$seen": true`) rather than whole-object assignment,
 * because whole-object means *replace*: sending `keywords: {$seen: true}` on a
 * flagged message silently unflags it. The client rarely knows the full current
 * set -- it knows the one thing the user just asked for -- so the patch form is
 * the one that matches what it actually knows.
 *
 * `null` removes. `true` adds. There is no other value for a keyword or a
 * mailbox membership.
 */
export type EmailPatch = Record<string, true | null>;

/** Mark a keyword present or absent on many messages at once. */
export function keywordPatch(keyword: string, present: boolean): EmailPatch {
  return { [`keywords/${keyword}`]: present ? true : null };
}

/**
 * Move messages between mailboxes.
 *
 * Archive, trash, junk and "move to folder" are all this call with different
 * ids -- there is no separate archive verb, upstream or here. Passing `from`
 * is what makes it a move rather than an additional filing; omit it to add a
 * label without removing one, which is meaningful on Gmail and a no-op
 * elsewhere.
 */
export function movePatch(to: string, from?: string): EmailPatch {
  const patch: EmailPatch = { [`mailboxIds/${to}`]: true };
  if (from && from !== to) patch[`mailboxIds/${from}`] = null;
  return patch;
}

/**
 * Apply the same patch to a set of messages in one account.
 *
 * One request per account rather than per message: forty rows selected and
 * archived is one round trip, which is the reason `Email/set` takes a map in
 * the first place.
 */
export async function updateEmails(
  accountId: string,
  ids: string[],
  patch: EmailPatch,
): Promise<SetOutcome> {
  if (ids.length === 0) return EMPTY;

  const update: Record<string, EmailPatch> = {};
  for (const id of ids) update[id] = patch;

  const results = await call([["Email/set", { accountId, update }, "s0"]]);
  return outcomeFrom(requireOk(results.get("s0"), "s0"), "updated", "notUpdated");
}

/**
 * Apply *different* patches to messages in one account.
 *
 * Needed by anything that toggles per row -- "mark all as read" over a
 * selection where some are already read would otherwise send pointless updates
 * the server has to reject or apply.
 */
export async function updateEach(
  accountId: string,
  patches: Record<string, EmailPatch>,
): Promise<SetOutcome> {
  if (Object.keys(patches).length === 0) return EMPTY;
  const results = await call([["Email/set", { accountId, update: patches }, "s0"]]);
  return outcomeFrom(requireOk(results.get("s0"), "s0"), "updated", "notUpdated");
}

/** One account's share of a batch that spans several. */
export interface AccountSet {
  update?: Record<string, EmailPatch>;
  destroy?: string[];
}

/**
 * Do the same thing in several accounts in one request.
 *
 * A unified mailbox is a merge of independent accounts, so a bulk action over
 * one is several `Email/set` calls -- but they belong in a single batch, which
 * is the reason JMAP has batches. Five accounts is five invocations and one
 * POST, not five POSTs and certainly not one per message.
 *
 * The result is per account because ids are per account: the same string can
 * name a different message in each, so merging the outcomes here would produce
 * a map whose keys mean two things. An account whose whole call was refused is
 * reported as every id in it failing, so a caller that rolls back per object
 * does not need a second code path for the case where the call itself died.
 */
export async function setAcrossAccounts(
  byAccount: Map<string, AccountSet>,
): Promise<Map<string, SetOutcome>> {
  const entries = [...byAccount].filter(
    ([, set]) => Object.keys(set.update ?? {}).length > 0 || (set.destroy?.length ?? 0) > 0,
  );
  const out = new Map<string, SetOutcome>();
  if (entries.length === 0) return out;

  const calls = entries.map(
    ([accountId, set], i): Invocation => [
      "Email/set",
      {
        accountId,
        ...(set.update ? { update: set.update } : {}),
        ...(set.destroy ? { destroy: set.destroy } : {}),
      },
      `s${i}`,
    ],
  );

  const results = await call(calls);
  entries.forEach(([accountId, set], i) => {
    const ids = [...Object.keys(set.update ?? {}), ...(set.destroy ?? [])];
    let args: Record<string, unknown>;
    try {
      args = requireOk(results.get(`s${i}`), `s${i}`);
    } catch (e) {
      const failed = new Map<string, SetError>();
      const detail = e instanceof SetFailed ? e.detail : {};
      for (const id of ids) {
        failed.set(id, {
          type: String(detail.type ?? "serverFail"),
          description: e instanceof Error ? e.message : String(e),
        });
      }
      out.set(accountId, { ok: [], failed, newState: null });
      return;
    }
    const updated = outcomeFrom(args, "updated", "notUpdated");
    const destroyed = outcomeFrom(args, "destroyed", "notDestroyed");
    out.set(accountId, {
      ok: [...updated.ok, ...destroyed.ok],
      failed: new Map([...updated.failed, ...destroyed.failed]),
      newState: updated.newState ?? destroyed.newState,
    });
  });
  return out;
}

/**
 * Delete messages.
 *
 * Really delete: the daemon turns this into a provider delete, not a move to
 * Trash. A UI offering "Delete" almost always wants {@link movePatch} to the
 * trash mailbox instead, and should reach this only for an explicit "delete
 * permanently" or for emptying a trash folder.
 */
export async function destroyEmails(accountId: string, ids: string[]): Promise<SetOutcome> {
  if (ids.length === 0) return EMPTY;
  const results = await call([["Email/set", { accountId, destroy: ids }, "s0"]]);
  return outcomeFrom(requireOk(results.get("s0"), "s0"), "destroyed", "notDestroyed");
}

// ---- drafts and sending -------------------------------------------------------

/** The subset of an `Email` a client may create. */
export interface DraftInput {
  mailboxIds: Record<string, true>;
  keywords?: Record<string, true>;
  from?: Email["from"];
  to?: Email["to"];
  cc?: Email["cc"];
  bcc?: Email["bcc"];
  replyTo?: Email["replyTo"];
  subject?: string | null;
  inReplyTo?: string[] | null;
  references?: string[] | null;
  bodyValues?: Record<string, { value: string }>;
  textBody?: { partId: string; type: string }[];
  htmlBody?: { partId: string; type: string }[];
  attachments?: unknown[];
}

/**
 * Save a draft, replacing the previous revision in the same call.
 *
 * Create-and-destroy together rather than as two requests, because the pair is
 * not atomic across two round trips: a save that creates and then fails to
 * destroy leaves two drafts, and one that destroys first and fails to create
 * loses what the user typed. In one call the daemon coalesces them into a
 * single provider operation.
 *
 * Returns the new server-assigned id, which the caller must hold as
 * `replacing` for the next save.
 */
export async function saveDraft(
  accountId: string,
  draft: DraftInput,
  replacing?: string | null,
): Promise<{ id: string; outcome: SetOutcome }> {
  const args: Record<string, unknown> = { accountId, create: { d: draft } };
  if (replacing) args.destroy = [replacing];

  const results = await call([["Email/set", args, "s0"]]);
  const ok = requireOk(results.get("s0"), "s0");
  const outcome = outcomeFrom(ok, "created", "notCreated");

  const created = (ok.created as Record<string, { id: string }> | undefined)?.d;
  if (!created) {
    const why = outcome.failed.get("d");
    throw new SetFailed(
      (why as unknown as Record<string, unknown>) ?? { type: "unknown", description: "the draft was not saved" },
    );
  }
  return { id: created.id, outcome };
}

/**
 * Send a message.
 *
 * The draft is created and submitted in one request, with `"#d"` referring to
 * the creation id from the first call. That is a *creation-id* reference (RFC
 * 8620 §5.3), not a result reference -- result references replace top-level
 * arguments and cannot reach inside `create`, so the more familiar `#ids`
 * syntax does not work here and fails in a way that reads like a server bug.
 *
 * `onSuccessUpdateEmail` is what files the message: it strips `$draft` and
 * moves it to Sent, but only if the submission succeeded. Doing that as a
 * separate call would file mail that was never sent.
 *
 * # The draft is never destroyed here, and that is a deliberate refusal
 *
 * This method has an `onSuccessDestroyEmail` available to it, and there is a
 * plausible argument for using it. Where the submission server files its own
 * copy in Sent -- which Gmail is documented to do for anything sent through
 * `smtp.gmail.com` -- the local draft is redundant the moment the send
 * succeeds, so moving it to Sent produces a duplicate. Gmail's IMAP also
 * refuses to move anything out of `[Gmail]/Drafts` at all, so that move cannot
 * be performed in the first place and fails permanently.
 *
 * It is still not used, because the argument rests on a premise one real send
 * contradicts. A message sent through that path left exactly one row in the
 * account -- the draft -- and `[Gmail]/Sent Mail` gained nothing that day, on
 * an account whose folder cursors were current and which ingested other mail
 * minutes later. Whatever the documentation says, no copy arrived. Destroying
 * the draft on the strength of the capability would have deleted the only
 * record that the message was ever sent.
 *
 * **A duplicate is visible and deletable; a vanished send is neither.** So the
 * failure this method is willing to have is a copy in the wrong folder, never
 * no copy at all. Filing correctly on a provider that refuses the move is an
 * append beside the message rather than a relocation of it, and it is not
 * something to guess at while what Gmail actually does is unknown.
 *
 * # When it is allowed to leave
 *
 * `release` is the undo window, and it is a property of the submission rather
 * than of this client, which is the only arrangement that cannot drift. RFC
 * 8621 gives three ways to say it and the daemon honours all three, in this
 * order of precedence:
 *
 *  - `"server"` sends neither field, and the daemon applies whatever hold its
 *    operator configured. This is the default and the honest one: the hold is
 *    enforced server-side, so leaving the number there means there is only one.
 *  - `{ sendAt }` names the release time outright. A client that offers its own
 *    undo affordance has to be the one choosing, and this is the field that
 *    exists for it -- the same field scheduled send uses.
 *  - `"immediate"` is `undoStatus: "final"`, RFC 8621's "I do not want an undo
 *    window". It is not the same as a `sendAt` of now: it says the client has
 *    already made its decision, which is what someone turning the window off
 *    has done.
 *
 * The returned `sendAt` is the server's answer, never the request. It is what
 * a countdown must be drawn from: whatever this client asked for, the moment
 * cancelling stops working is the one the daemon wrote down.
 */
export type Release = "server" | "immediate" | { sendAt: string };

export async function sendEmail(
  accountId: string,
  draft: DraftInput,
  opts: {
    identityId: string;
    sentMailboxId?: string;
    replacing?: string | null;
    release?: Release;
  },
): Promise<{ emailId: string; submissionId: string; sendAt: string | null }> {
  const setArgs: Record<string, unknown> = { accountId, create: { d: draft } };
  if (opts.replacing) setArgs.destroy = [opts.replacing];

  const release = opts.release ?? "server";
  const submission: Record<string, unknown> = { emailId: "#d", identityId: opts.identityId };
  if (release === "immediate") submission.undoStatus = "final";
  else if (release !== "server") submission.sendAt = release.sendAt;

  const onSuccess: Record<string, true | null> = {
    "keywords/$draft": null,
    "keywords/$seen": true,
  };
  if (opts.sentMailboxId) {
    // Said in full, per RFC 8620 §5.3: adding Sent AND removing every folder
    // the draft was staged in. A per-key `true` patch *adds* a membership and
    // touches nothing else, so naming only the Sent mailbox would leave the
    // message in Drafts too on a spec-correct server. This daemon historically
    // read the patch as a whole-set replacement -- it collects only the `true`
    // keys, so these nulls are ignored there and behaviour is identical --
    // which is exactly what makes this safe to say now and required once the
    // daemon starts reading the patch as the spec means it.
    onSuccess[`mailboxIds/${opts.sentMailboxId}`] = true;
    for (const mailboxId of Object.keys(draft.mailboxIds)) {
      if (mailboxId !== opts.sentMailboxId) onSuccess[`mailboxIds/${mailboxId}`] = null;
    }
  }

  const calls: Invocation[] = [
    ["Email/set", setArgs, "s0"],
    [
      "EmailSubmission/set",
      {
        accountId,
        create: { sub: submission },
        onSuccessUpdateEmail: { "#sub": onSuccess },
      },
      "s1",
    ],
  ];

  const results = await call(calls, [SUBMISSION]);

  const created = requireOk(results.get("s0"), "s0");
  const emailId = (created.created as Record<string, { id: string }> | undefined)?.d?.id;
  if (!emailId) {
    const why = (created.notCreated as Record<string, SetError> | undefined)?.d;
    throw new SetFailed((why as unknown as Record<string, unknown>) ?? { type: "unknown" });
  }

  const submitted = requireOk(results.get("s1"), "s1");
  const enqueued = (
    submitted.created as Record<string, { id: string; sendAt?: string }> | undefined
  )?.sub;
  const submissionId = enqueued?.id;
  if (!submissionId) {
    const why = (submitted.notCreated as Record<string, SetError> | undefined)?.sub;
    // The draft exists and the send did not happen. Saying so matters: the
    // message is not lost, and the user should find it in Drafts rather than
    // retype it.
    throw new SetFailed(
      (why as unknown as Record<string, unknown>) ?? {
        type: "unknown",
        description: "the message was saved as a draft but not sent",
      },
    );
  }

  return { emailId, submissionId, sendAt: enqueued?.sendAt ?? null };
}

/** One submission, as much of it as anything in this client needs. */
export interface Submission {
  id: string;
  emailId: string | null;
  /** The server's release time, in epoch milliseconds. */
  sendAt: number | null;
  undoStatus: string;
  /** `yes`, `no`, `queued` or `unknown` -- and never a guess. */
  delivered: string | null;
  /** What the mail server or the daemon said, when either said anything. */
  smtpReply: string | null;
  rcptTo: string[];
}

/**
 * Read submissions back.
 *
 * `undoStatus` is `pending` until the daemon's outbox drains, so a UI that
 * closes the composer on a 200 is reporting "queued", not "sent" -- this is how
 * it finds out which one it was. Anything other than an explicit rejection
 * comes back as `unknown` rather than failed, because a connection that dropped
 * after `DATA` is indistinguishable from one that was refused, and claiming a
 * message failed when it may have been delivered is the worse of the two
 * mistakes.
 *
 * `null` ids means every submission the account still has, which is bounded --
 * the daemon keeps a window of finished ones and prunes the rest. That is the
 * only way to see a submission this page did not create: a message parked by a
 * *previous* session is mail that silently did not arrive, and a client that
 * only ever polled its own sends would never show it.
 */
export async function submissions(
  accountId: string,
  ids: string[] | null,
): Promise<Submission[]> {
  if (ids !== null && ids.length === 0) return [];

  const results = await call([["EmailSubmission/get", { accountId, ids }, "g0"]], [SUBMISSION]);
  const args = results.get("g0");
  if (!args || args.__error) return [];

  return ((args.list as Record<string, unknown>[] | undefined) ?? []).map((item) => {
    // Keyed by recipient. Every entry of one submission carries the same
    // verdict from this daemon -- it makes one SMTP attempt for the whole
    // message -- so the first is the answer for all of them.
    const status = item.deliveryStatus as
      | Record<string, { delivered?: string; smtpReply?: string }>
      | undefined;
    const first = status ? Object.values(status)[0] : undefined;
    const envelope = item.envelope as { rcptTo?: { email: string }[] } | undefined;
    const sendAt = item.sendAt ? Date.parse(String(item.sendAt)) : NaN;
    return {
      id: String(item.id),
      emailId: item.emailId === null || item.emailId === undefined ? null : String(item.emailId),
      sendAt: Number.isNaN(sendAt) ? null : sendAt,
      undoStatus: String(item.undoStatus ?? "pending"),
      delivered: first?.delivered ?? null,
      smtpReply: first?.smtpReply?.trim() ? first.smtpReply.trim() : null,
      rcptTo: (envelope?.rcptTo ?? []).map((r) => r.email),
    };
  });
}

/** What an attempt to withdraw a send did. */
export type Withdrawn = "done" | "tooLate" | "gone";

/**
 * Withdraw a send that has not been handed to the mail server yet.
 *
 * The three outcomes are not interchangeable and the caller must not collapse
 * them. `tooLate` is the daemon's `cannotUnsend`: the SMTP dialogue has begun,
 * the message may already be in someone's mailbox, and there is no honest
 * report but that. Showing "cancelled" for it would tell somebody their message
 * was never sent when it may well have been -- which is the one mistake an undo
 * button is able to make.
 *
 * The message is not lost either way. A cancelled submission leaves the draft
 * exactly where it was: `onSuccessUpdateEmail` is applied by the drain *after*
 * a successful send, so nothing moved it out of Drafts to begin with.
 */
export async function cancelSubmission(accountId: string, id: string): Promise<Withdrawn> {
  const results = await call(
    [["EmailSubmission/set", { accountId, update: { [id]: { undoStatus: "canceled" } } }, "s0"]],
    [SUBMISSION],
  );
  const args = requireOk(results.get("s0"), "s0");

  if ((args.updated as Record<string, unknown> | undefined)?.[id] !== undefined) return "done";

  const why = (args.notUpdated as Record<string, SetError> | undefined)?.[id];
  if (why?.type === "cannotUnsend") return "tooLate";
  if (why?.type === "notFound") return "gone";
  // Anything else is a refusal this client did not anticipate, and the safe
  // reading of "the server would not withdraw it" is that it did not.
  throw new SetFailed((why as unknown as Record<string, unknown>) ?? { type: "unknown" });
}

// ---- optimism ------------------------------------------------------------------

/**
 * Apply a change to a local list now, and put it back if the server disagrees.
 *
 * `apply` runs immediately and returns the rollback. `commit` runs the request.
 * Anything the server reports as failed is passed to `revert` so the caller can
 * restore just those objects -- and a whole-request failure reverts everything.
 *
 * The shape is a function rather than a pattern to copy because the failure it
 * prevents is one of omission: the rollback that was never written, on the path
 * nobody tested, for the error nobody expected.
 */
export async function optimistically(
  apply: () => void,
  commit: () => Promise<SetOutcome>,
  revert: (failedIds: string[], errors: Map<string, SetError>) => void,
): Promise<SetOutcome> {
  apply();
  try {
    const outcome = await commit();
    if (outcome.failed.size > 0) {
      revert([...outcome.failed.keys()], outcome.failed);
    }
    return outcome;
  } catch (e) {
    // A whole-request failure: nothing was applied server-side, so everything
    // goes back. `revert` is called with an empty id list meaning "all of it".
    revert([], new Map());
    throw e;
  }
}
